/**
 * F-131 — the stored result, joined to the catalogue, in the shapes the screens render.
 *
 * The server computes the score once (hermes `_shared/prometheus/roadmap-score.ts`) and stores
 * a `RoadmapResult`: points by id, pillar totals, the constraint, the suggested order and the
 * finance KPI rows. This joins that to the roadmap points (names, order, prerequisites) and
 * formats the KPI rows; it computes no score. Pure, so it is tested on fixtures.
 */
import {
  CONSTRAINT_PRIORITY_LABELS,
  KPI_STATUS_LABELS,
  lifecycleLabelFor,
  MATURITY_LABELS,
  PILLAR_LABELS,
  type CatalogueAsset,
  type Constraint,
  type PillarCode,
  type PillarRoadmap,
  type PrimeScore,
  type RoadmapAsset,
  type RoadmapKpi,
} from '@/lib/prime-score';
import type {
  ResultPlatform, ResultKpi, RoadmapResult } from '@/api/roadmap';
import type { Metric } from '@/types/tracking-view';
import { formatKpiTarget, formatKpiValue } from './finance-kpis';
import { moveResponse, suggestedRole, type SuggestedMoveRole } from './suggested-moves';

/**
 * Why the Results track is grey, or `connected` when the rows carry real figures.
 * `no_figures`: the org has an active Xero connection but the cockpit has not built its
 * financial model yet, so the rows are shown waiting, never told to connect.
 */
export type ResultsTrackState = 'flag_off' | 'no_access' | 'not_connected' | 'no_figures' | 'connected';

/**
 * The gates in order: the flag, then the viewer's finance access, then the connection, then
 * figures. Access comes before the connection so an empty KPI list (withheld rows) never turns
 * into a "connect Xero" line for someone who cannot.
 */
export function deriveResultsState(input: {
  flagOn: boolean;
  allowed: boolean;
  connected: boolean;
  hasModel: boolean;
}): ResultsTrackState {
  if (!input.flagOn) return 'flag_off';
  if (!input.allowed) return 'no_access';
  if (!input.connected) return 'not_connected';
  if (!input.hasModel) return 'no_figures';
  return 'connected';
}

/**
 * The cockpit-shaped metric behind a stored row, so the row can be handed to the cockpit's own
 * confidence primitives (`summaryForMetric`, `ConfidenceDot`). Only the fields the server kept;
 * history and styles are empty because the roadmap draws none.
 */
export function toMetric(kpi: ResultKpi): Metric {
  return {
    id: kpi.id,
    domain: 'financial',
    pack: 'universal',
    label: kpi.label,
    value: kpi.value,
    unit: kpi.unit,
    history: [],
    ...(kpi.target ? { target: kpi.target } : {}),
    ...(kpi.thresholds ? { thresholds: kpi.thresholds } : {}),
    status: kpi.status,
    level: 1,
    source: (kpi.source as Metric['source']) ?? 'xero',
    styles: [],
    dataQuality: kpi.dataQuality,
    ...(kpi.reconciled !== undefined ? { reconciled: kpi.reconciled } : {}),
    ...(kpi.contamination
      ? {
        contamination: {
          severity: kpi.contamination.severity as NonNullable<Metric['contamination']>['severity'],
          note: kpi.contamination.note,
          affectedRoles: [],
          fixAccountId: kpi.contamination.fixAccountId ?? null,
        },
      }
      : {}),
  };
}

/** Xero as the finance platform, for a result stored before the catalogue carried platforms. */
export const XERO_PLATFORM: ResultPlatform = {
  code: 'xero',
  name: 'Xero',
  connectPath: '/finance',
  status: 'live',
  pillars: ['finance'],
};

/** Why an unmeasured row is greyed, in the builder's words, from the platform it names. */
function greyedReasonFor(kpi: ResultKpi, platform: ResultPlatform | undefined): string | null {
  if (kpi.measured !== false) return null;
  // The platform is named on the group's "Coming soon" line, so the row says only that it waits.
  if (platform?.status === 'planned') return 'coming soon';
  if (platform) return `not computed from ${platform.name} yet`;
  return 'no source yet';
}

/**
 * A stored row as the screen reads it. A row stored before migration 580 carries no code,
 * platform or frequency: it is one of the five Xero vitals, measured, monthly.
 */
export function toRoadmapKpi(kpi: ResultKpi, platforms: ResultPlatform[] = []): RoadmapKpi {
  const status = kpi.rag ?? 'not_connected';
  const platformCode = kpi.platform === undefined ? 'xero' : kpi.platform;
  const platform = platformCode ? platforms.find((p) => p.code === platformCode) : undefined;
  const measured = kpi.measured ?? true;
  return {
    id: kpi.id,
    pillar: kpi.pillar,
    name: kpi.label,
    unit: kpi.unit,
    target: formatKpiTarget(kpi),
    dataSource: platform?.name ?? (platformCode === 'xero' ? 'Xero' : 'No source yet'),
    frequency: kpi.frequency === undefined ? 'Monthly' : kpi.frequency ?? '',
    constraintSignal: '',
    status,
    statusLabel: KPI_STATUS_LABELS[status],
    value: formatKpiValue(kpi),
    counted: kpi.counted,
    metric: toMetric(kpi),
    code: kpi.code ?? kpi.id,
    platform: platformCode,
    measured,
    greyedReason: measured ? null : greyedReasonFor({ ...kpi, measured }, platform),
    platformName: platform?.name ?? (platformCode === 'xero' ? 'Xero' : null),
    coming: platform?.status === 'planned',
  };
}

/** The coaching move types, in the words the suggested order labels them with (D1). */
export const MOVE_TYPE_LABELS: Record<string, string> = {
  build: 'Build',
  confirm: 'Confirm',
  ask: 'Tell us',
  verify: 'Still true?',
  connect: 'Connect Xero',
  complete_diagnostic: 'Finish diagnostic',
};

export function moveTypeLabel(moveType: string): string {
  return MOVE_TYPE_LABELS[moveType] ?? moveType;
}

/** A point in the suggested order, with where the coaching engine put it. */
export interface SuggestedAsset extends RoadmapAsset {
  /** Null on a result stored before roles existed, and for a role this client does not know. */
  suggestedRole: SuggestedMoveRole | null;
}

export interface RoadmapView {
  score: PrimeScore;
  pillars: PillarRoadmap[];
  constraint: Constraint | null;
  /** Why there is no constraint, when there is none and the server said why. */
  constraintNote: string | null;
  /**
   * The server's suggested order, resolved to points: the coaching move, then its real successor
   * (at most one), then where each other pillar would start. Each row carries its own role, so a
   * surface never reads the order positionally.
   */
  suggested: SuggestedAsset[];
  /** The coaching move type behind each suggested point. Empty on a result stored before D1. */
  suggestedMoveTypes: Map<string, string>;
  assetsById: Map<string, RoadmapAsset>;
  /** Points the business has never been assessed on (level null). */
  notAssessed: Set<string>;
  /** Points at Documented or better on evidence alone, awaiting the owner's claim. */
  /** The one "State as of". */
  computedAt: string;
  results: ResultsTrackState;
  /** The KPI rows of every pillar (the measured ones withheld from a viewer without finance access). */
  kpis: RoadmapKpi[];
  /** The rows by catalogue code, for a point's KPI steps. */
  kpisByCode: Map<string, RoadmapKpi>;
  /** When the figures shown were last pulled from Xero. */
  fetchedAt: string | null;
  /** The reporting period the figures are true as at ('2026-08'), or null. */
  period: string | null;
  /** The catalogue's platforms (Xero live; others planned), for each pillar's Results half. */
  platforms: ResultPlatform[];
}

export function buildRoadmapView(
  result: RoadmapResult,
  catalogue: CatalogueAsset[],
  opts: { flagOn: boolean },
): RoadmapView {
  const nameById = new Map(catalogue.map((a) => [a.id, a.name]));
  const notAssessed = new Set<string>();
  const assetsById = new Map<string, RoadmapAsset>();

  for (const a of catalogue) {
    const point = result.points[a.id];
    const level = point?.level ?? null;
    const maturity = level ?? 1;
    if (level === null) notAssessed.add(a.id);
    const prerequisiteMet = point?.prerequisiteMet ?? !a.prerequisiteId;
    assetsById.set(a.id, {
      ...a,
      maturity,
      maturityLabel: MATURITY_LABELS[maturity] ?? `Level ${maturity}`,
      state: point?.state ?? (prerequisiteMet ? 'available' : 'locked'),
      prerequisiteName: a.prerequisiteId ? nameById.get(a.prerequisiteId) ?? null : null,
      prerequisiteMet,
    });
  }

  const results = deriveResultsState({
    flagOn: opts.flagOn,
    // Fail closed: the RPC always sets this; a payload without it withholds the rows.
    allowed: result.results.viewerHasFinanceAccess === true,
    connected: result.results.state !== 'not_connected',
    hasModel: result.results.state === 'connected',
  });
  // A result stored before migration 580 names no platforms: its five rows are Xero's.
  const platforms = result.results.platforms ?? [XERO_PLATFORM];
  const kpis = result.results.kpis.map((k) => toRoadmapKpi(k, platforms));
  const kpisByCode = new Map(kpis.map((k) => [k.code, k]));

  const pillars: PillarRoadmap[] = result.pillars.map((rp) => ({
    pillar: rp.pillar,
    label: rp.label ?? PILLAR_LABELS[rp.pillar],
    pillarScore: rp.pillarScore,
    assets: [...assetsById.values()]
      .filter((a) => a.pillar === rp.pillar)
      .sort((a, b) => a.orderIndex - b.orderIndex),
    assetHealthPoints: rp.assetHealthPoints,
    assetHealthMax: rp.assetHealthMax,
    inPlaceCount: rp.inPlaceCount,
    totalCount: rp.totalCount,
    kpis: kpis.filter((k) => k.pillar === rp.pillar),
    kpiHealthPoints: rp.kpiHealthPoints,
    kpiHealthMax: rp.kpiHealthMax,
    kpiCountedCount: rp.kpiCountedCount,
    kpiTotalCount: rp.kpiTotalCount,
  }));

  const constraintAsset = result.constraint ? assetsById.get(result.constraint.assetId) : undefined;
  const constraint: Constraint | null = result.constraint && constraintAsset
    ? {
      pillar: result.constraint.pillar,
      pillarLabel: PILLAR_LABELS[result.constraint.pillar],
      asset: constraintAsset,
      priority: result.constraint.priority,
      priorityLabel: CONSTRAINT_PRIORITY_LABELS[result.constraint.priority],
      reason: result.constraint.reason,
      response: moveResponse(result.constraint.response),
    }
    : null;

  // The role travels on the row rather than in a second map, so every reader of the order has it
  // without the page having to pass one more prop down.
  const rolesById = new Map<string, SuggestedMoveRole | null>(
    (result.suggestedMoves ?? []).map((move) => [move.assetId, suggestedRole(move.role)]),
  );
  const suggested = result.suggestedOrder
    .map((id) => {
      const asset = assetsById.get(id);
      return asset === undefined
        ? undefined
        : { ...asset, suggestedRole: rolesById.get(id) ?? null };
    })
    .filter((a): a is SuggestedAsset => a !== undefined);
  const suggestedMoveTypes = new Map<string, string>(
    (result.suggestedMoves ?? [])
      .filter((move) => assetsById.has(move.assetId))
      .map((move) => [move.assetId, move.moveType]),
  );

  const score: PrimeScore = {
    assetHealthPoints: result.score.assetHealthPoints,
    assetHealthMax: result.score.assetHealthMax,
    kpiHealthPoints: result.score.kpiHealthPoints,
    kpiHealthMax: result.score.kpiHealthMax,
    bpsTotal: result.score.bpsTotal,
    bpsMax: result.score.bpsMax,
    lifecycleStage: result.score.lifecycleStage,
    // Labelled here from the stored code, so a label change never waits for a recompute.
    lifecycleLabel: lifecycleLabelFor(result.score.lifecycleStage),
    // The radar reads the roadmap's own pillar scores (points and KPIs, 0–100 per pillar), the
    // same numbers as the pillar panels and the next-move card. It used to draw the diagnostic's
    // question-derived percentages, so Finance read 91 on the chart and 3 on the card (Tim,
    // 10 Sep 2026: "43 and 91 make no sense").
    pillarScores: pillarScoresFrom(Object.fromEntries(result.pillars.map((rp) => [rp.pillar, rp.pillarScore]))),
  };

  return {
    score,
    pillars,
    constraint,
    constraintNote: result.constraintNote ?? null,
    suggested,
    suggestedMoveTypes,
    assetsById,
    notAssessed,
    computedAt: result.computedAt,
    results,
    kpis,
    kpisByCode,
    fetchedAt: result.results.fetchedAt,
    period: result.results.period ?? null,
    platforms,
  };
}

/** Six keys, always. A pillar with no score reads 0 on the radar. */
export function pillarScoresFrom(raw: Record<string, number> | null | undefined): Record<PillarCode, number> {
  const out = {} as Record<PillarCode, number>;
  for (const pillar of Object.keys(PILLAR_LABELS) as PillarCode[]) {
    const value = raw?.[pillar];
    out[pillar] = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
  return out;
}
